import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { ShieldQuestion, Loader2, CheckCircle2, XCircle, ChevronDown, ChevronUp } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  declined: "bg-slate-200 text-slate-700",
  expired: "bg-slate-100 text-slate-500",
  withdrawn: "bg-slate-100 text-slate-500",
  superseded: "bg-slate-100 text-slate-500",
};

const COST_LABEL = (a) => {
  if (a.cost_mode === "estimate") return `Estimate: ${a.cost_currency} ${a.cost_amount}`;
  if (a.cost_mode === "not_to_exceed") return `Approved maximum (not to exceed): ${a.cost_currency} ${a.cost_amount}`;
  return "No cost specified";
};

export default function PortalAuthorizations({ vesselId, onDecided }) {
  const [list, setList] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    api
      .get(`/portal/authorizations?vessel_id=${vesselId}`)
      .then(({ data }) => setList(data.authorizations || []))
      .catch(() => setList([]));
  }, [vesselId]);

  useEffect(() => { load(); }, [load]);

  if (list === null)
    return (
      <div className="bg-white rounded-xl p-4 border border-slate-100 flex justify-center" data-testid="portal-auth-loading">
        <Loader2 className="h-5 w-5 animate-spin text-[#D4AF37]" />
      </div>
    );

  if (list.length === 0) return null;

  return (
    <div className="bg-white rounded-xl p-4 border border-slate-100" data-testid="portal-authorizations">
      <div className="text-sm font-medium text-[#051329] mb-3 flex items-center gap-1">
        <ShieldQuestion className="h-4 w-4 text-amber-500" /> Authorization requests
      </div>
      <ul className="space-y-3">
        {list.map((a) => (
          <li key={a.id} className="border border-slate-150 rounded-lg" data-testid={`portal-auth-${a.id}`}>
            <button
              data-testid={`portal-auth-toggle-${a.id}`}
              onClick={() => setOpenId(openId === a.id ? null : a.id)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left"
            >
              <span className="min-w-0">
                <span className="text-sm text-[#051329] font-medium block truncate">{a.requested_action}</span>
                <span className="text-xs text-slate-500">{a.issue_summary}</span>
              </span>
              <span className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${STATUS_STYLE[a.status] || "bg-slate-100"}`}
                  data-testid={`portal-auth-status-${a.id}`}>
                  {a.status}
                </span>
                {openId === a.id ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
              </span>
            </button>
            {openId === a.id && (
              <AuthorizationDetail
                authId={a.id}
                status={a.status}
                onChanged={() => { load(); onDecided && onDecided(); }}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

const AuthorizationDetail = ({ authId, status, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [ack, setAck] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/portal/authorizations/${authId}`).then(({ data }) => setDetail(data)).catch(() => {});
  }, [authId]);

  const decide = async (decision) => {
    setBusy(true);
    setErr("");
    try {
      await api.post(`/portal/authorizations/${authId}/decide`, {
        decision,
        reason,
        acknowledge_scope: decision === "approve" ? ack : false,
      });
      onChanged();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not record your decision. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!detail)
    return <div className="px-3 pb-3 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-[#D4AF37]" /></div>;

  const ev = detail.evidence || {};
  const decided = detail.status !== "pending";

  return (
    <div className="px-3 pb-3 space-y-3 border-t border-slate-100 pt-3" data-testid={`portal-auth-detail-${authId}`}>
      {/* Evidence */}
      {ev.observation && (
        <div className="text-sm">
          <span className="text-slate-500">Observation: </span>
          <b className="capitalize">{(ev.observation.value || "not observed").replace(/_/g, " ")}</b>
          {ev.observation.note ? ` — ${ev.observation.note}` : ""}
        </div>
      )}
      {ev.photo_ids?.length > 0 && (
        <div className="grid grid-cols-3 gap-2" data-testid={`portal-auth-photos-${authId}`}>
          {ev.photo_ids.slice(0, 6).map((pid) => (
            <img key={pid} alt="evidence" src={`${BACKEND}/api/portal/photos/${pid}`} className="w-full h-20 object-cover rounded-md" />
          ))}
        </div>
      )}
      {ev.comparison?.deltas?.length > 0 && (
        <div className="text-xs text-slate-600" data-testid={`portal-auth-comparison-${authId}`}>
          <span className="text-slate-500">Since previous visit: </span>
          {ev.comparison.deltas.map((d) => d.label).join(", ")}
        </div>
      )}
      {ev.comparison?.coverage_gap_keys?.length > 0 && (
        <div className="text-xs text-amber-700">Coverage gaps: {ev.comparison.coverage_gap_keys.join(", ")}</div>
      )}

      {/* Requested action + scope + cost */}
      <div className="rounded-lg bg-slate-50 p-3 space-y-1.5">
        <div className="text-xs text-slate-500">{detail.action_label}</div>
        <div className="text-sm text-[#051329]"><b>Requested:</b> {detail.requested_action}</div>
        <div className="text-sm text-[#051329]"><b>Scope:</b> {detail.scope}</div>
        <div className="text-sm text-[#051329]" data-testid={`portal-auth-cost-${authId}`}><b>Cost:</b> {COST_LABEL(detail)}</div>
        {detail.expires_at && <div className="text-xs text-slate-500">Expires: {detail.expires_at.slice(0, 10)}</div>}
      </div>

      <p className="text-[11px] text-slate-500">{detail.bounded_authority_note}</p>
      <p className="text-[11px] text-slate-400">{detail.semantics_note}</p>

      {decided ? (
        <div className="text-sm" data-testid={`portal-auth-decided-${authId}`}>
          <span className={`font-semibold ${detail.decision === "approved" ? "text-emerald-700" : "text-slate-600"}`}>
            {detail.status === "approved" ? "You authorized this" : detail.status === "declined" ? "You declined this" : `Status: ${detail.status}`}
          </span>
          {detail.decided_at && <span className="text-slate-500"> · {detail.decided_at.slice(0, 16).replace("T", " ")}</span>}
          {detail.decision_reason && <div className="text-xs text-slate-500 mt-1">Note: {detail.decision_reason}</div>}
        </div>
      ) : (
        <div className="space-y-2">
          <textarea
            data-testid={`portal-auth-reason-${authId}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Optional note with your decision…"
            rows={2}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
          />
          <label className="flex items-start gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              data-testid={`portal-auth-ack-${authId}`}
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
              className="mt-0.5"
            />
            I have reviewed the evidence and scope above and authorize HMC to proceed within this exact scope and cost only.
          </label>
          {err && <p className="text-xs text-red-600" data-testid={`portal-auth-error-${authId}`}>{err}</p>}
          <div className="flex gap-2">
            <button
              data-testid={`portal-auth-approve-${authId}`}
              disabled={!ack || busy}
              onClick={() => decide("approve")}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 disabled:opacity-40 text-white py-2.5 text-sm font-semibold"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Authorize
            </button>
            <button
              data-testid={`portal-auth-decline-${authId}`}
              disabled={busy}
              onClick={() => decide("decline")}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 text-slate-700 py-2.5 text-sm font-semibold"
            >
              <XCircle className="h-4 w-4" /> Decline
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
